import { Callout, HelpLink, Related } from "./parts";

export function UsersTeamsAndSignIn() {
  return (
    <>
      <p>
        Every request through the proxy is billed to a person. The proxy learns who that is from the credential the
        developer sends. There are three kinds, and a customer's proxy accepts one or more of them (new installations
        accept AWS sign-in and API keys). Sign-in methods are set up on the customer's <strong>Sign-in</strong> tab, in
        its sub-tabs <strong>Status &amp; methods</strong>, <strong>Identity providers</strong>, <strong>Cognito</strong>,{" "}
        <strong>AWS sign-in</strong> and <strong>Admin access</strong>. Its <strong>Keys</strong> tab has{" "}
        <strong>API keys</strong>, <strong>Key settings</strong> (API key sign-in, AWS access keys, the key policy) and{" "}
        <strong>Trusted tokens</strong>. Each sub-tab saves only its own settings.
      </p>
      <table>
        <thead>
          <tr>
            <th>Kind</th>
            <th>Developer needs</th>
            <th>Lasts</th>
            <th>Best for</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>AWS sign-in (recommended)</td>
            <td>Their usual AWS sign-in and the small <code>nexus-aws-token</code> helper</td>
            <td>15 minutes, renewed by the helper</td>
            <td>Everyone who already signs in to AWS</td>
          </tr>
          <tr>
            <td>OIDC</td>
            <td>A token from the company's identity provider</td>
            <td>Whatever the provider sets</td>
            <td>Single sign-on through a provider not federated into AWS</td>
          </tr>
          <tr>
            <td>API key</td>
            <td>
              An <code>nxk_…</code> key you issue
            </td>
            <td>Up to 365 days</td>
            <td>Scripts, CI and people without AWS access</td>
          </tr>
        </tbody>
      </table>

      <h2 id="users-and-teams">Users and teams</h2>
      <ul>
        <li>
          You rarely create users by hand. A user is created the first time a new AWS principal or OIDC subject calls the
          proxy, named by its AWS identity or its OIDC subject and email. The customer's <strong>People</strong> tab lists
          them on its <strong>Users</strong> sub-tab; its <strong>Teams</strong> sub-tab holds the teams.
        </li>
        <li>
          A <strong>team</strong> groups users for reports (usage by team). AWS sign-in callers get a team from the
          team mappings below; OIDC callers from the team claim of their token. A user with no team is reported without
          one.
        </li>
        <li>
          A <strong>disabled</strong> user's requests are refused from then on, like a suspended customer but for one
          person. Their history is kept.
        </li>
      </ul>

      <h2 id="aws-sign-in">AWS sign-in</h2>
      <p>
        Developers run <code>aws sso login</code> (or however they normally sign in to AWS) and the{" "}
        <code>nexus-aws-token</code> helper turns that into a short-lived proxy token. No key is issued or stored. The
        proxy asks AWS who signed the token and records the user as their AWS account, role and session name.
      </p>
      <p>Who gets in is set on the Sign-in tab's <strong>AWS sign-in</strong> sub-tab:</p>
      <ul>
        <li>
          <strong>Allowed AWS accounts</strong>: further 12-digit account IDs, each added on its own row. The installation's own account
          is always allowed and is not listed.
        </li>
        <li>
          <strong>Allowed principals</strong>: patterns a caller must match, such as <code>permission-set/Developers</code>,{" "}
          <code>role/ci-*</code>, <code>user/alice</code> or <code>federated-user/*</code>. <code>*</code> matches
          anything. Until you save this sub-tab, only IAM Identity Center users of the installation's own account get in (
          <code>permission-set/*</code>). An <strong>empty</strong> list lets in every principal of an allowed account,
          service roles included, so widen one pattern at a time instead.
        </li>
        <li>
          <strong>Team mappings</strong>: pattern → team. The first match decides which team a caller's usage counts
          under.
        </li>
      </ul>
      <p>
        <strong>Save</strong> replaces the whole setting. The customer's proxies pick it up within about five minutes. The
        AWS account root user is always refused.
      </p>
      <Callout>
        Developers need two values from you: the proxy URL and the <strong>installation id</strong> (the token's audience),
        from the customer's AWS accounts tab. A token made for one installation does not work at another.
      </Callout>

      <h2 id="oidc">OIDC providers</h2>
      <p>
        Register the customer's identity provider so its tokens are accepted: the issuer URL (https only), the audience the
        tokens carry, and, if the provider uses other names, which claims hold the user, email, team and customer id.
      </p>
      <ul>
        <li>The issuer must be reachable when you save it; Nexus reads its signing keys then.</li>
        <li>
          Each token must name this customer in its org claim (<code>org_id</code> by default), matching the customer's id.
        </li>
        <li>Deleting a provider stops its tokens at the proxies' next configuration refresh.</li>
      </ul>

      <h2 id="proxy-sign-in">Proxy sign-in</h2>
      <p>
        Two settings on the customer's <strong>Keys</strong> tab reach every proxy of the customer in its configuration, within
        about a minute: how many keys one person may hold (<strong>Key settings</strong>), and the trusted token issuers (
        <strong>Trusted tokens</strong>).
      </p>
      <ul>
        <li>
          <strong>Keys per person</strong> (1 to 50, default 10): each device a person signs in from holds one key. At the limit
          the person revokes one on the proxy's keys page first.
        </li>
        <li>
          <strong>Trusted tokens</strong>: a CI system or another identity provider whose signed tokens the proxy accepts. By
          default the proxy trades such a token once for a machine key it can revoke.
        </li>
        <li>
          No secret is entered here. An identity provider's client secret is entered by the customer's admin on the proxy and
          stays in the customer's AWS account. Who serves sign-in (AllCode or the proxy) is changed by AllCode staff with the
          flip, never in these settings.
        </li>
      </ul>

      <h2 id="api-keys">API keys</h2>
      <p>
        Issue and revoke them on the customer's <strong>Keys → API keys</strong> sub-tab. People's issued keys, one per
        machine, are kept per installation: open one under <strong>AWS accounts</strong>.
      </p>
      <ul>
        <li>
          A <strong>user key</strong> (<code>nxk_…</code>) belongs to one user; developers send it as their bearer token. You
          can limit it to some models; empty means every model the installation serves.
        </li>
        <li>
          A <strong>service key</strong> (<code>nxs_…</code>) is for integrations such as a BI tool, with scopes: read
          reports, read prompts, or admin writes.
        </li>
        <li>
          Every key must have an expiry date at most 365 days away. There is no key that never expires.
        </li>
        <li>
          The key is shown <strong>once</strong>, when it is created. Afterwards only its first characters are shown, so
          you can recognise it. Pass it on through a password manager, never chat or email.
        </li>
        <li>
          <strong>Revoke</strong> stops a key within about a minute (the proxies' cache time).
        </li>
      </ul>

      <Related to={["installations", "claude-code-and-codex", "adding-a-customer"]} />
    </>
  );
}
